import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Ninja } from "./types";

// Session shape and sessionStorage keys are unchanged from the pre-React app,
// so an in-flight login survives the migration.
interface AuthContextValue {
  facilityId: string;
  studentId: string | null;
  currentNinja: Ninja | null;
  isLoggedIn: boolean;
  isLocked: boolean;
  /** Stores the session after a successful login (or the dev quick-login). */
  startSession: (session: { facilityId: string; studentId: string }) => void;
  setCurrentNinja: (ninja: Ninja | null) => void;
  setLocked: (locked: boolean) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function loadNinja(): Ninja | null {
  const raw = sessionStorage.getItem("currentNinja");
  if (!raw) return null;
  try {
    return JSON.parse(raw) as Ninja;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [facilityId, setFacilityId] = useState(
    () => sessionStorage.getItem("facilityId") || "",
  );
  const [studentId, setStudentId] = useState(() =>
    sessionStorage.getItem("studentId"),
  );
  const [currentNinja, setCurrentNinjaState] = useState<Ninja | null>(loadNinja);
  const [isLocked, setLocked] = useState(false);

  const startSession = useCallback(
    (session: { facilityId: string; studentId: string }) => {
      sessionStorage.setItem("facilityId", session.facilityId);
      sessionStorage.setItem("studentId", session.studentId);
      setFacilityId(session.facilityId);
      setStudentId(session.studentId);
      setLocked(false);
    },
    [],
  );

  const setCurrentNinja = useCallback((ninja: Ninja | null) => {
    if (ninja) {
      sessionStorage.setItem("currentNinja", JSON.stringify(ninja));
    } else {
      sessionStorage.removeItem("currentNinja");
    }
    setCurrentNinjaState(ninja);
  }, []);

  const logout = useCallback(() => {
    sessionStorage.removeItem("studentId");
    sessionStorage.removeItem("currentNinja");
    sessionStorage.removeItem("cn_token");
    setStudentId(null);
    setCurrentNinjaState(null);
    setLocked(false);
  }, []);

  // The API client fires this when a request comes back 401 (expired/revoked
  // CN token). Clearing the session flips isLoggedIn, and RequireLogin routes
  // back to the login page.
  useEffect(() => {
    window.addEventListener("auth:unauthorized", logout);
    return () => window.removeEventListener("auth:unauthorized", logout);
  }, [logout]);

  useEffect(() => {
    const onLocked = () => setLocked(true);
    window.addEventListener("auth:locked", onLocked);
    return () => window.removeEventListener("auth:locked", onLocked);
  }, []);

  const value = useMemo(
    () => ({
      facilityId,
      studentId,
      currentNinja,
      isLoggedIn: studentId !== null,
      isLocked,
      startSession,
      setCurrentNinja,
      setLocked,
      logout,
    }),
    [facilityId, studentId, currentNinja, isLocked, startSession, setCurrentNinja, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
